import { AuthCard } from '@auth/components';
import type { AuthProviderConfig } from '@auth/domain';
import { useAuthProviders, useAuthState, useLogin } from '@auth/hooks';
import { constructPostAuthRedirect, extractRedirectFromParams } from '@auth/utils';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { routes } from '@core/routes';
import { ButtonType, Button } from '@soteria/design-system-ve';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import * as styles from './LoginPage.css';

function LoginPageComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthState();
  const { providers, loading: providersLoading, errors: providersErrors, refetch } = useAuthProviders();
  const { initiateLogin, error: loginError } = useLogin();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(routes.communityGroups.root);
    }
  }, [isAuthenticated, navigate]);

  const searchParams = new URLSearchParams(location.search);
  const hasRedirectParam = extractRedirectFromParams(searchParams) !== null;

  const handleRetry = () => {
    refetch();
  };

  async function handleSelectProvider(provider: AuthProviderConfig) {
    const redirectUri = `${window.location.origin}${routes.loginCallback}`;
    const postAuthRedirectUrl = constructPostAuthRedirect(searchParams);

    await initiateLogin(provider, redirectUri, postAuthRedirectUrl);
  }

  const title = hasRedirectParam ? 'Authentication Required' : 'Sign in';
  const description = hasRedirectParam
    ? 'You need to sign in to access this page. Select an authentication provider to continue'
    : 'Select an authentication provider to continue';

  const errorBannerTitle = providersErrors ? 'An error occurred while fetching the providers:' : loginError ? 'An error occurred during login:' : undefined;

  return (
    <div className={styles.loginPageContainer}>
      <AuthCard
        description={description}
        error={providersErrors || loginError}
        errorBannerTitle={errorBannerTitle}
        isLoading={providersLoading}
        loadingText="Loading authentication providers..."
        title={title}
        onRetry={handleRetry}
      >
        {/* Provider List */}
        <div className={styles.providerList}>
          {providers.map(provider => (
            <Button key={provider.id} buttonType={ButtonType.Primary} className={styles.providerButton} onClick={() => handleSelectProvider(provider)}>
              {provider.logoUrl && <img alt={`${provider.name} logo`} className={styles.providerLogo} src={provider.logoUrl} />}
              {`Continue with ${provider.name}`}
            </Button>
          ))}
        </div>
      </AuthCard>
    </div>
  );
}

export const LoginPage = withAppInsightsTracking(LoginPageComponent, 'LoginPage');
